import { GameState, GameEvent, CardDef, PlayerSide } from '../types.js';
import { logCardLink } from '../shared/log-markers.js';

// ── Event-based game log narration ─────────────────────────────────────
//
// The engine's `GameEvent[]` already describes what actually happened
// (X was returned to hand, Y took N damage, Z drew a card). The log
// used to render the ability's *rule template* instead — a mouthful
// like "Return opponent's weakest ally to opponent's hand. If so,
// return player's strongest character to player's hand." — which
// forced the reader to reconstruct which specific cards moved.
//
// This helper walks the events and produces concrete, past-tense
// phrases from the human player's perspective. "Your", "opponent's",
// and directional damage/heal/spirit lines flip automatically based
// on the affected side. Card names are wrapped in {{card:...}}
// markers so the log renderer keeps them clickable.
//
// Contract: returns '' when no narratable events fired. Callers use
// that to decide whether to push a log line at all.

/** Player perspective: reader is always the human ('player'). */
function nameOf(instanceId: string, state: GameState, catalog: Map<string, CardDef>): string {
  const inst = state.cardInstances.get(instanceId);
  if (!inst) return 'a card';
  const def = catalog.get(inst.cardId);
  if (!def) return 'a card';
  return logCardLink(def);
}

function ownerOf(instanceId: string, state: GameState): PlayerSide | null {
  return state.cardInstances.get(instanceId)?.owner ?? null;
}

/** Possessive from the reader's POV. */
function possessive(side: PlayerSide): string {
  return side === 'player' ? 'your' : "opponent's";
}

/** Subject from the reader's POV. */
function subject(side: PlayerSide): string {
  return side === 'player' ? 'you' : 'opponent';
}

function joinList(parts: string[]): string {
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`;
}

/** Turn a single event into a phrase, or null if we don't narrate this
 *  kind of event in the log. `null` phrases are skipped entirely. */
function phraseForEvent(
  ev: GameEvent,
  state: GameState,
  catalog: Map<string, CardDef>,
): string | null {
  switch (ev.type) {
    case 'card-returned': {
      const name = nameOf(ev.instanceId, state, catalog);
      return `Returned ${name} to ${possessive(ev.player)} hand`;
    }
    case 'character-ko': {
      const name = nameOf(ev.instanceId, state, catalog);
      return ev.player === 'player' ? `Destroyed your ${name}` : `Destroyed ${name}`;
    }
    case 'power-changed': {
      if (ev.amount === 0) return null;
      const name = nameOf(ev.instanceId, state, catalog);
      const abs = Math.abs(ev.amount);
      if (ev.amount > 0) return `Boosted ${name} by ${abs} power`;
      return `Reduced ${name} by ${abs} power`;
    }
    case 'armor-changed': {
      if (ev.amount === 0) return null;
      const name = nameOf(ev.instanceId, state, catalog);
      const abs = Math.abs(ev.amount);
      if (ev.amount > 0) return `Granted ${name} ${abs} armor`;
      return `Stripped ${abs} armor from ${name}`;
    }
    case 'hp-changed': {
      if (ev.amount === 0) return null;
      if (ev.amount < 0) {
        const dmg = Math.abs(ev.amount);
        return ev.player === 'player'
          ? `Dealt ${dmg} damage to you`
          : `Dealt ${dmg} damage to opponent`;
      }
      return `Healed ${subject(ev.player)} for ${ev.amount}`;
    }
    case 'spirit-changed': {
      const delta = ev.newSpirit - ev.oldSpirit;
      if (delta === 0) return null;
      const abs = Math.abs(delta);
      const who = ev.player === 'player' ? 'You' : 'Opponent';
      return delta > 0 ? `${who} gained ${abs} spirit` : `${who} lost ${abs} spirit`;
    }
    case 'card-discarded': {
      const name = nameOf(ev.instanceId, state, catalog);
      return `Discarded ${name}`;
    }
    case 'card-milled': {
      const name = nameOf(ev.instanceId, state, catalog);
      return `Milled ${name}`;
    }
    case 'character-exhausted': {
      const owner = ownerOf(ev.instanceId, state);
      const name = nameOf(ev.instanceId, state, catalog);
      return owner === 'player' ? `Exhausted your ${name}` : `Exhausted ${name}`;
    }
    case 'character-locked': {
      const owner = ownerOf(ev.instanceId, state);
      const name = nameOf(ev.instanceId, state, catalog);
      return owner === 'player' ? `Locked your ${name}` : `Locked ${name}`;
    }
    case 'character-refreshed': {
      const owner = ownerOf(ev.instanceId, state);
      const name = nameOf(ev.instanceId, state, catalog);
      return owner === 'player' ? `Readied your ${name}` : `Readied ${name}`;
    }
    case 'ability-silenced': {
      const name = nameOf(ev.instanceId, state, catalog);
      return `Silenced ${name}`;
    }
    case 'clock-changed': {
      // No prior-value in the event, so we only say the direction if
      // we can infer from the source, and otherwise fall back to a
      // neutral phrasing.
      return `Advanced the clock to ${ev.clock}`;
    }
    default:
      return null;
  }
}

/** Collapse runs of same-shape events for compactness. Concretely:
 *  - N `power-changed` with the same signed amount → one phrase
 *  - N `armor-changed` with the same signed amount → one phrase
 *  - N `card-drawn` in a row from the same player → one "drew N cards"
 *  Other events stay one-per-phrase. */
export function narrateAbilityEvents(
  events: GameEvent[],
  state: GameState,
  catalog: Map<string, CardDef>,
): string {
  const phrases: string[] = [];

  let i = 0;
  while (i < events.length) {
    const ev = events[i];

    // Group card-drawn runs by player.
    if (ev.type === 'card-drawn') {
      const player = ev.player;
      let count = 0;
      while (i < events.length && events[i].type === 'card-drawn' && (events[i] as { player: PlayerSide }).player === player) {
        count++;
        i++;
      }
      const who = player === 'player' ? 'You' : 'Opponent';
      const drew = count === 1 ? 'drew a card' : `drew ${count} cards`;
      phrases.push(`${who} ${drew}`);
      continue;
    }

    // Group same-signed power-changed runs.
    if (ev.type === 'power-changed' && ev.amount !== 0) {
      const sign = Math.sign(ev.amount);
      const abs = Math.abs(ev.amount);
      const names: string[] = [];
      while (i < events.length) {
        const e = events[i];
        if (e.type !== 'power-changed') break;
        if (Math.sign(e.amount) !== sign) break;
        if (Math.abs(e.amount) !== abs) break;
        names.push(nameOf(e.instanceId, state, catalog));
        i++;
      }
      const verb = sign > 0 ? 'Boosted' : 'Reduced';
      phrases.push(`${verb} ${joinList(names)} by ${abs} power`);
      continue;
    }

    // Group same-signed armor-changed runs.
    if (ev.type === 'armor-changed' && ev.amount !== 0) {
      const sign = Math.sign(ev.amount);
      const abs = Math.abs(ev.amount);
      const names: string[] = [];
      while (i < events.length) {
        const e = events[i];
        if (e.type !== 'armor-changed') break;
        if (Math.sign(e.amount) !== sign) break;
        if (Math.abs(e.amount) !== abs) break;
        names.push(nameOf(e.instanceId, state, catalog));
        i++;
      }
      if (sign > 0) phrases.push(`Granted ${joinList(names)} ${abs} armor`);
      else phrases.push(`Stripped ${abs} armor from ${joinList(names)}`);
      continue;
    }

    const phrase = phraseForEvent(ev, state, catalog);
    if (phrase) phrases.push(phrase);
    i++;
  }

  if (phrases.length === 0) return '';
  // Compound-effect phrasing: chain with ", then " so the reader can
  // see the ordering. First letter of subsequent clauses lowercases
  // for readability ("Returned X, then returned Y").
  return phrases.map((p, idx) => idx === 0 ? p : lowercaseFirst(p)).join(', then ');
}

function lowercaseFirst(s: string): string {
  if (!s) return s;
  return s.charAt(0).toLowerCase() + s.slice(1);
}
